import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Wallet,
    Send,
    Instagram,
    Facebook,
    Mail,
    Rocket,
    Sparkles,
    CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const XIcon = (props) => (
    <svg
        {...props}
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
);

const TikTokIcon = (props) => (
    <svg
        {...props}
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.26Z" />
    </svg>
);

const FIELDS = [
    {
        name: "wallet",
        label: "Solana Wallet",
        placeholder: "e.g. 4dAwbA1Gx...pump",
        Icon: Wallet,
        hint: "Where your airdrop will be sent",
        type: "text",
    },
    {
        name: "x_handle",
        label: "X (Twitter)",
        placeholder: "@spce_100x",
        Icon: XIcon,
        type: "text",
    },
    {
        name: "telegram_handle",
        label: "Telegram",
        placeholder: "@username",
        Icon: Send,
        type: "text",
    },
    {
        name: "tiktok_handle",
        label: "TikTok",
        placeholder: "@username",
        Icon: TikTokIcon,
        type: "text",
    },
    {
        name: "instagram_handle",
        label: "Instagram",
        placeholder: "@username",
        Icon: Instagram,
        type: "text",
    },
    {
        name: "facebook_handle",
        label: "Facebook",
        placeholder: "Your Facebook name or URL",
        Icon: Facebook,
        type: "text",
    },
    {
        name: "email",
        label: "Email (optional)",
        placeholder: "you@example.com",
        Icon: Mail,
        type: "email",
        optional: true,
    },
];

const EMPTY = FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {});

export default function GiveawayForm() {
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [totalEntries, setTotalEntries] = useState(null);

    const fetchStats = async () => {
        try {
            const { data } = await axios.get(`${API}/giveaway/stats`);
            setTotalEntries(data.total_entries);
        } catch {
            /* silent */
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const handleChange = (e) =>
        setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = { ...form };
            // strip empty email
            if (!payload.email) delete payload.email;
            await axios.post(`${API}/giveaway/entry`, payload);
            setSuccess(true);
            toast.success("Entry submitted. You're in the running, astronaut.");
            setForm(EMPTY);
            fetchStats();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409) {
                toast.error("This wallet is already entered.");
            } else if (status === 422 && Array.isArray(detail)) {
                const first = detail[0];
                const field = first?.loc?.[first.loc.length - 1] || "field";
                toast.error(`Invalid ${field}: ${first?.msg ?? "check input"}`);
            } else {
                toast.error("Submission failed. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div
                data-testid="giveaway-form-success"
                className="relative bg-[#0B0B1A] border border-[#A3FF12]/40 rounded-3xl p-10 sm:p-14 text-center overflow-hidden"
                style={{
                    boxShadow:
                        "0 0 32px rgba(163,255,18,0.18), inset 0 0 24px rgba(163,255,18,0.06)",
                }}
            >
                <div
                    aria-hidden
                    className="absolute -top-24 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-[#A3FF12]/20 blur-3xl"
                />
                <div className="relative">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#A3FF12]/15 border border-[#A3FF12]/50 mb-6">
                        <CheckCircle2 size={28} className="text-[#A3FF12]" />
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A3FF12] mb-3">
                        // Entry Locked In
                    </div>
                    <h3 className="font-display text-2xl sm:text-4xl font-black uppercase tracking-tight text-white">
                        You're in the running
                    </h3>
                    <p className="mt-5 font-mono text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
                        Stay active on all 5 channels. Winners are announced
                        live on Telegram once the campaign ends.
                    </p>
                    <button
                        type="button"
                        data-testid="giveaway-form-submit-another"
                        onClick={() => setSuccess(false)}
                        className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF] hover:text-white transition-colors"
                    >
                        Submit another wallet →
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            data-testid="giveaway-form"
            className="relative bg-[#0B0B1A] border border-white/10 rounded-3xl p-7 sm:p-10 overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-[#A3FF12]/10 blur-3xl"
            />
            <div
                aria-hidden
                className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-[#A3FF12]/60 to-transparent"
            />

            <div className="relative">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A3FF12] mb-3">
                            // Entry Form
                        </div>
                        <h3 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-white">
                            Claim your slot
                        </h3>
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5">
                        <Sparkles size={12} className="text-[#A3FF12]" />
                        <span
                            data-testid="giveaway-entry-count"
                            className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-300"
                        >
                            {totalEntries === null
                                ? "Live count loading…"
                                : `${totalEntries.toLocaleString()} astronauts joined`}
                        </span>
                    </div>
                </div>

                <p className="font-mono text-xs text-slate-400 mt-3 leading-relaxed max-w-xl">
                    Drop your wallet + social handles below. We verify proof of
                    action before announcing winners on Telegram.
                </p>

                {/* Fields */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {FIELDS.map((f) => {
                        const Icon = f.Icon;
                        const spanFull =
                            f.name === "wallet" || f.name === "email";
                        return (
                            <div
                                key={f.name}
                                className={spanFull ? "sm:col-span-2" : ""}
                            >
                                <Label
                                    htmlFor={`giveaway-${f.name}`}
                                    className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-400 flex items-center gap-2"
                                >
                                    <Icon className="w-3.5 h-3.5 text-[#A3FF12]" />
                                    {f.label}
                                </Label>
                                <Input
                                    id={`giveaway-${f.name}`}
                                    data-testid={`giveaway-input-${f.name}`}
                                    name={f.name}
                                    type={f.type}
                                    placeholder={f.placeholder}
                                    value={form[f.name]}
                                    onChange={handleChange}
                                    required={!f.optional}
                                    autoComplete="off"
                                    className="mt-2 h-12 bg-black/60 border-white/10 text-white placeholder:text-slate-600 font-mono text-sm focus-visible:ring-0 focus-visible:border-[#A3FF12]/70 focus-visible:shadow-[0_0_18px_rgba(163,255,18,0.25)] rounded-xl"
                                />
                                {f.hint && (
                                    <div className="font-mono text-[10px] tracking-wide text-slate-500 mt-1.5">
                                        {f.hint}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Submit */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 max-w-sm">
                        By submitting you agree to be contacted on Telegram if
                        you win. No spam, ever.
                    </p>
                    <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="giveaway-form-submit"
                        className="h-14 px-8 rounded-full bg-[#A3FF12] hover:bg-[#A3FF12]/90 text-black font-display font-black uppercase tracking-tight text-sm disabled:opacity-60 transition-all hover:-translate-y-0.5"
                        style={{
                            boxShadow:
                                "0 0 28px rgba(163,255,18,0.55), 0 0 60px rgba(163,255,18,0.25)",
                        }}
                    >
                        <Rocket size={18} className="mr-2" />
                        {submitting ? "Launching…" : "Enter Giveaway"}
                    </Button>
                </div>
            </div>
        </form>
    );
}
